import { Box, Typography, Paper } from '@mui/material';
import { API, graphqlOperation } from 'aws-amplify';
import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import {
  listCashAccountsCustom,
  listAccountTransactionDetailsv3,
  listaccounttransactiondetailstotals,
  listAccountMasterv2,
} from '../../../../graphql';
import { useReportContext } from '../ReportContext';
import { useSelector } from 'react-redux';
import { useSearchParams, useParams } from 'react-router-dom';
// import Ledgers from '../../../newReports/reportTable/ledgers';
import { defaultRowsPerPage } from '../../../../utils/common';
import LedgerNew from './ledgerNew';

const BankCashBook = () => {
  const dashboardData = useSelector((state) => state?.DashboardDataReducer);
  const { startTimeStamp, endTimeStamp } = dashboardData?.report;
  const { updateReportData, loading, setLoading, reportData } = useReportContext();
  const [searchParams] = useSearchParams();
  const { id: reportId } = useParams();
  const [tableData, setTableData] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [dataCount, setDataCount] = useState(30);
  const previousReportIdRef = useRef(reportId);
  const previousIdsRef = useRef('');

  // Read includeOpeningBalance from URL, default to true if not present - memoized
  const includeOpeningBalance = useMemo(() => {
    const param = searchParams.get('includeOpeningBalance');
    return param !== null ? param === 'true' : true;
  }, [searchParams]);
  useEffect(() => {
    const apiCall = async () => {
      setLoading(true);

      const resCashAccounts = await API.graphql(graphqlOperation(listCashAccountsCustom));
      console.log(resCashAccounts, 'resCashAccounts');

      const cashAccounts = resCashAccounts.data.listCashAccounts.map((cash) => ({
        ...cash,
        accountgroupname: 'Cash in Hand',
      }));
      const bankAccounts = resCashAccounts.data.listBankAccounts.map((bank) => ({
        ...bank,
        accountgroupname: 'Bank Accounts',
      }));
      console.log(cashAccounts, bankAccounts, 'bankAccounts');

      // Pass data to Header component via context
      const combinedData = [...cashAccounts, ...bankAccounts];
      updateReportData({
        allAccounts: combinedData,
        loading: false,
      });

      setLoading(false);
    };
    apiCall();
  }, []);

  // Fetch transaction data for selected account IDs
  const idsParam = searchParams.get('ids');
  console.log(idsParam, 'idsParam');

  // Helper function to fetch account transactions
  const fetchAccountTransactions = useCallback(
    async (accountId) => {
      try {
        const [response, resCount] = await Promise.all([
          API.graphql(
            graphqlOperation(listAccountTransactionDetailsv3, {
              accountid: accountId,
              columnname: 'enteredtimestamp',
              searcharray: '[]',
              searchmode: 0,
              ordertype: 'asc',
              openingbalance: includeOpeningBalance ? 1 : 0,
              startdate: startTimeStamp,
              enddate: endTimeStamp,
              rowfrom: 0,
              limitno: 25,
            })
          ),
          API.graphql(
            graphqlOperation(listaccounttransactiondetailstotals, {
              accountid: accountId,
              startdate: startTimeStamp,
              openingbalance: includeOpeningBalance ? 1 : 0,
              enddate: endTimeStamp,
            })
          ),
        ]);

        return {
          accountid: accountId,
          data: response.data.listAccountTransactionDetailsv3,
          count: resCount.data.listaccounttransactiondetailstotals[0]?.transactioncount || 0,
          credit: resCount.data.listaccounttransactiondetailstotals[0]?.totalcredit || 0,
          debit: resCount.data.listaccounttransactiondetailstotals[0]?.totaldebit || 0,
          total: resCount.data.listaccounttransactiondetailstotals[0]?.closingbalance || 0,
        };
      } catch (err) {
        console.error(`Error fetching transactions for account ${accountId}:`, err);
        return null;
      }
    },
    [includeOpeningBalance, startTimeStamp, endTimeStamp]
  );

  // Helper function to update table data with fetched results
  const updateTableDataWithResults = useCallback((results, selectedIds) => {
    const newItems = results.map((result) => (result.status === 'fulfilled' ? result.value : null)).filter(Boolean);

    if (newItems.length > 0) {
      setTableData((prevItems) => {
        // Ensure we only keep items that are in the URL
        const filteredItems = prevItems.filter((item) => selectedIds.includes(String(item.accountid)));

        // Create a map for quick lookup
        const itemsMap = new Map(filteredItems.map((item) => [String(item.accountid), item]));

        // Add or update new items
        newItems.forEach((newItem) => {
          itemsMap.set(String(newItem.accountid), newItem);
        });

        // Return array maintaining order from selectedIds
        return selectedIds.map((id) => itemsMap.get(id)).filter(Boolean);
      });
    }
  }, []);

  // useEffect 1: Handle idsParam changes (only fetch for newly added IDs)
  useEffect(() => {
    previousReportIdRef.current = reportId;

    if (!idsParam) {
      // If no ids param, clear all data and no need to fetch
      setTableData([]);
      setLoading(false);
      previousIdsRef.current = '';
      return;
    }

    const decodedIds = decodeURIComponent(idsParam);
    const selectedIds = decodedIds
      .split(',')
      .filter(Boolean)
      .map((id) => id.trim());

    // Get previous IDs from ref
    const previousIds = previousIdsRef.current
      ? decodeURIComponent(previousIdsRef.current)
          .split(',')
          .filter(Boolean)
          .map((id) => id.trim())
      : [];

    // Find newly added IDs (in current list but not in previous list)
    const newlyAddedIds = selectedIds.filter((id) => !previousIds.includes(id));

    // Find removed IDs (in previous list but not in current list)
    const removedIds = previousIds.filter((id) => !selectedIds.includes(id));

    // Remove items that are not in the URL (no API call needed)
    if (removedIds.length > 0) {
      setTableData((prevItems) => {
        return prevItems.filter((item) => selectedIds.includes(String(item.accountid)));
      });
    }

    // Only fetch data for newly added IDs
    if (newlyAddedIds.length === 0) {
      // No new IDs to fetch, just update the ref
      previousIdsRef.current = idsParam;
      return;
    }

    // Show loader when fetching data for new IDs
    setLoading(true);

    // Fetch data only for newly added IDs
    const fetchPromises = newlyAddedIds.map((accountId) => fetchAccountTransactions(accountId));

    // Wait for all fetches to complete and batch update state once
    Promise.allSettled(fetchPromises).then((results) => {
      updateTableDataWithResults(results, selectedIds);
      setLoading(false);
      // Update the ref after fetching is complete
      previousIdsRef.current = idsParam;
    });
  }, [idsParam]);

  // useEffect 2: Handle filter changes (fetch all current IDs)
  useEffect(() => {
    if (!idsParam) {
      return;
    }

    const decodedIds = decodeURIComponent(idsParam);
    const selectedIds = decodedIds
      .split(',')
      .filter(Boolean)
      .map((id) => id.trim());

    if (selectedIds.length === 0) {
      return;
    }

    // Show loader when fetching data for all IDs
    setLoading(true);

    // Fetch data for all current IDs when filters change
    const fetchPromises = selectedIds.map((accountId) => fetchAccountTransactions(accountId));

    // Wait for all fetches to complete and batch update state once
    Promise.allSettled(fetchPromises).then((results) => {
      updateTableDataWithResults(results, selectedIds);
      setLoading(false);
    });
  }, [includeOpeningBalance, startTimeStamp, endTimeStamp]);

  // Memoize ledgersData to prevent unnecessary re-renders
  const ledgersData = useMemo(() => reportData?.allAccounts || [], [reportData?.allAccounts]);

  // Memoize callbacks to prevent child re-renders
  const handleLedgerChangePage = useCallback(
    async (event, newPage, accountId) => {
      setLoading(true);
      try {
        const response = await API.graphql(
          graphqlOperation(listAccountTransactionDetailsv3, {
            accountid: accountId,
            columnname: 'enteredtimestamp',
            searcharray: '[]',
            searchmode: 0,
            ordertype: 'asc',
            openingbalance: includeOpeningBalance ? 1 : 0,
            startdate: startTimeStamp,
            enddate: endTimeStamp,
            rowfrom: newPage * rowsPerPage,
            limitno: rowsPerPage,
          })
        );

        setTableData((prevItems) =>
          prevItems.map((item) => {
            return String(item.accountid) === String(accountId)
              ? {
                  ...item,
                  data: [...response.data.listAccountTransactionDetailsv3],
                }
              : item;
          })
        );
      } catch (err) {
        console.error('Error changing page:', err);
      } finally {
        setLoading(false);
      }
    },
    [includeOpeningBalance, startTimeStamp, endTimeStamp, rowsPerPage]
  );

  const handleLedgerChangeRowsPerPage = useCallback(
    async (event, accountId) => {
      const newRowsPerPage = parseInt(event.target.value, 10);
      setRowsPerPage(newRowsPerPage);
      setLoading(true);

      try {
        const response = await API.graphql(
          graphqlOperation(listAccountTransactionDetailsv3, {
            accountid: accountId,
            startdate: startTimeStamp,
            openingbalance: includeOpeningBalance ? 1 : 0,
            enddate: endTimeStamp,
            rowfrom: 0,
            limitno: newRowsPerPage,
          })
        );

        if (response.data.listAccountTransactionDetailsv3.length > 0) {
          setTableData((prevItems) =>
            prevItems.map((item) => {
              return String(item.accountid) === String(accountId)
                ? {
                    ...item,
                    data: [...response.data.listAccountTransactionDetailsv3],
                  }
                : item;
            })
          );
        }
      } catch (err) {
        console.error('Error changing rows per page:', err);
      } finally {
        setLoading(false);
      }
    },
    [includeOpeningBalance, startTimeStamp, endTimeStamp]
  );

  // Memoize ledgersData to prevent unnecessary re-renders
  // const ledgersData = useMemo(() => reportData?.allAccounts || [], [reportData?.allAccounts]);

  // Memoize selectedIdsOrder and sortedTableData
  const selectedIdsOrder = useMemo(() => {
    if (!idsParam) return [];
    return decodeURIComponent(idsParam)
      .split(',')
      .filter(Boolean)
      .map((id) => id.trim())
      .reverse(); // Reverse to show last selected first
  }, [idsParam]);

  // Sort tableData to match the order of selectedIdsOrder (descending - last selected first)
  const sortedTableData = useMemo(() => {
    return [...tableData].sort((a, b) => {
      const indexA = selectedIdsOrder.indexOf(String(a.accountid));
      const indexB = selectedIdsOrder.indexOf(String(b.accountid));
      // If not found in order, put at end
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
  }, [tableData, selectedIdsOrder]);

  // Memoize callbacks passed to children
  const handleSetTableData = useCallback((updater) => {
    setTableData(updater);
  }, []);

  const handleSetDataCount = useCallback((value) => {
    setDataCount(value);
  }, []);

  return (
    <>
      {sortedTableData?.map((data) => (
        <LedgerNew
          key={data.accountid}
          accountid={data.accountid}
          data={data.data}
          setTableData={handleSetTableData}
          includeOpeningBalance={includeOpeningBalance}
          count={data.count}
          ledgersData={ledgersData}
          setdataCount={handleSetDataCount}
          rowsPerPage={rowsPerPage}
          totalDebit={data.debit}
          totalCredit={data.credit}
          totalbalance={data.total}
          handleChangePage={(event, newPage) => handleLedgerChangePage(event, newPage, data.accountid)}
          handleChangeRowsPerPage={(event) => handleLedgerChangeRowsPerPage(event, data.accountid)}
        />
      ))}
    </>
  );
};

export default BankCashBook;
